import { useEffect, useMemo } from 'react';

import { MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  DayTypeSetting,
  selectDayTypeSetting,
} from 'state/originDestinationSlices';
import {
  selectCurrentProjectInfo,
  selectDayType,
  setDayType,
} from 'state/workflowSlice';

import { ExtendedDayTypes } from 'appConstants';

import styles from './ODMatrix.module.css';

type DayTypeOption = {
  value: ExtendedDayTypes;
  label: string;
  middleEastDayType?: boolean;
};

const kDisplayableDayTypes: Record<DayTypeSetting, DayTypeOption[]> = {
  [DayTypeSetting.COMBINED]: [
    { value: ExtendedDayTypes.AllDays, label: 'All days' },
  ],
  [DayTypeSetting.BY_DAY_TYPE]: [
    {
      value: ExtendedDayTypes.Weekday,
      label: ExtendedDayTypes.Weekday,
      middleEastDayType: false,
    },
    {
      value: ExtendedDayTypes.Saturday,
      label: ExtendedDayTypes.Saturday,
      middleEastDayType: false,
    },
    {
      value: ExtendedDayTypes.Sunday,
      label: ExtendedDayTypes.Sunday,
      middleEastDayType: false,
    },
    {
      value: ExtendedDayTypes.MeWeekday,
      label: ExtendedDayTypes.Weekday,
      middleEastDayType: true,
    },
    {
      value: ExtendedDayTypes.Friday,
      label: ExtendedDayTypes.Friday,
      middleEastDayType: true,
    },
    {
      value: ExtendedDayTypes.Saturday,
      label: ExtendedDayTypes.Saturday,
      middleEastDayType: true,
    },
  ],
};

export default function ODDayTypeSelector() {
  const dispatch = useAppDispatch();
  const dayTypeSetting = useAppSelector(selectDayTypeSetting);
  const dayType = useAppSelector(selectDayType);
  const userProject = useAppSelector(selectCurrentProjectInfo);

  const currValues = useMemo(() => {
    const options = kDisplayableDayTypes[dayTypeSetting];

    if (dayTypeSetting === DayTypeSetting.COMBINED) {
      return options;
    }

    const relevantOptions = options.filter(
      (el) => el.middleEastDayType === userProject?.uses_middle_east_day_types,
    );

    return [
      ...relevantOptions,
      ...kDisplayableDayTypes[DayTypeSetting.COMBINED],
    ];
  }, [dayTypeSetting, userProject?.uses_middle_east_day_types]);

  const defaultVal = currValues[0]?.value;

  useEffect(() => {
    const isValid = currValues.some((el) => el.value === dayType);

    if (!isValid && defaultVal) {
      dispatch(setDayType(defaultVal));
    }
  }, [dayType, currValues, defaultVal]);

  const handleChange = (e: SelectChangeEvent<ExtendedDayTypes>) => {
    dispatch(setDayType(e.target.value as ExtendedDayTypes));
  };
  return (
    <Select
      className={styles.selector}
      value={dayType || defaultVal}
      onChange={handleChange}
      size="small"
      disabled={dayTypeSetting === DayTypeSetting.COMBINED}
    >
      {currValues.map((option) => (
        <MenuItem
          key={`${option.value}-${String(option.middleEastDayType)}`}
          value={option.value}
        >
          <Typography variant="body2">{option.label}</Typography>
        </MenuItem>
      ))}
    </Select>
  );
}
